// Office.js integration seam. Everything host-specific lives here so the rest
// of the app is plain React and renders in a normal browser for verification.
//
// Host context is optional: in a plain browser we fall back to a mock "current
// email" so the flows stay demoable. Those fallbacks are gated on inOutlook() —
// inside Outlook we never invent an item, because the pane can be launched from
// the top-level toolbar with nothing selected.
//
// SSO getAccessToken, the ItemChanged subscription and displayReplyForm are all
// implemented below (the old "phase 2-4 TODO" note here was stale).

export interface OfficeState {
  /** True when running inside an Outlook host (vs. a plain browser). */
  inHost: boolean;
}

/** Is the Outlook host live RIGHT NOW?
 *
 *  Deliberately a function, not a latched boolean. It used to be
 *  `let hostReady = false`, set once from Office.onReady and then read by nine
 *  call sites. That made a startup race permanently disabling: if the flag was
 *  read before (or latched without) readiness, SSO, ItemChanged,
 *  displayReplyForm and the item accessors all stayed dead for the WHOLE session,
 *  and the pane silently ran in browser-fallback mode inside real Outlook. That is
 *  exactly the "works sometimes, not others" symptom — a cold pane launch from the
 *  top-level toolbar loses the race more often than a warm in-message launch.
 *
 *  Computing it from live state means a slow host simply works as soon as it's up,
 *  and no single mis-timed read can poison the session. */
function hostAvailable(): boolean {
  const Office = (globalThis as any).Office;
  return !!Office?.context?.mailbox;
}

/** Public read of the same live state, for the "this only runs inside Outlook"
 *  gate in App.tsx. Same reasoning as above: a function, never a cached boolean. */
export function isInOutlook(): boolean {
  return hostAvailable();
}

/** Longest we'll wait for the host before treating this as a plain browser. */
const HOST_READY_TIMEOUT_MS = 8000;
/** How often to re-check while waiting. Office.onReady is the primary signal;
 *  polling is the belt-and-braces one, because a missed/late onReady must not be
 *  able to strand the pane. */
const HOST_POLL_MS = 250;

const lateReadyCallbacks: (() => void)[] = [];
let watching = false;

/** Run cb once the host is available — immediately if it already is, otherwise as
 *  soon as it shows up (even long after initOffice() resolved). */
export function onHostReady(cb: () => void): void {
  if (hostAvailable()) {
    cb();
    return;
  }
  lateReadyCallbacks.push(cb);
  watchForHost();
}

/** Poll until the host appears, then flush the waiting callbacks. Idempotent. */
function watchForHost(): void {
  if (watching) return;
  watching = true;
  const startedAt = Date.now();
  const timer = setInterval(() => {
    const up = hostAvailable();
    if (!up && Date.now() - startedAt < HOST_READY_TIMEOUT_MS) return;
    clearInterval(timer);
    watching = false;
    if (up) {
      console.info(`[office] host became available after ${Date.now() - startedAt}ms — re-syncing`);
      for (const cb of lateReadyCallbacks.splice(0)) cb();
    } else {
      console.info("[office] no Outlook host detected — running in browser mode");
      lateReadyCallbacks.length = 0;
    }
  }, HOST_POLL_MS);
}

/** Resolve once we know whether we're in a host. Safe to call more than once
 *  (React StrictMode double-invokes effects in development, and the previous
 *  implementation left the second call's promise pending forever). */
export function initOffice(): Promise<OfficeState> {
  if (hostAvailable()) return Promise.resolve({ inHost: true });

  const OfficeGlobal = (globalThis as any).Office;
  // Plain browser with no Office.js at all: don't make local dev wait out the
  // backstop before the pane syncs.
  if (!OfficeGlobal) return Promise.resolve({ inHost: false });

  return new Promise((resolve) => {
    const Office = OfficeGlobal;
    let done = false;
    // Declared before onReady: Office.js calls an onReady registered after it has
    // already initialised synchronously, and finish() would then hit `timer` in its
    // temporal dead zone, throw, and leave this promise rejected for good.
    let timer: ReturnType<typeof setInterval> | undefined;
    const finish = () => {
      if (done) return;
      done = true;
      clearInterval(timer);
      const inHost = hostAvailable();
      console.info(`[office] init complete — inHost=${inHost}`);
      resolve({ inHost });
    };

    // Primary signal.
    Office?.onReady?.(() => finish());

    // Backstop + poll: covers a missing/late onReady and a genuinely non-host page.
    const startedAt = Date.now();
    if (!done) {
      timer = setInterval(() => {
        if (hostAvailable() || Date.now() - startedAt >= HOST_READY_TIMEOUT_MS) finish();
      }, HOST_POLL_MS);
    }
  });
}

/** True when we're really running inside Outlook (not a plain browser).
 *
 *  This gates the demo fallbacks below. Getting it wrong is not cosmetic: the
 *  add-in can be launched from the top-level Outlook toolbar with NO message
 *  selected (SupportsNoItemContext), and the fallbacks used to fire in that
 *  case too — so the pane claimed the user was reading "PTO request for the
 *  first week of August" from "Dana Williams", and the backend then hydrated
 *  that fake id from mock data and summarized an email that does not exist in
 *  the user's mailbox. In a host we must report "no message" honestly. */
function inOutlook(): boolean {
  return hostAvailable();
}

/** The message the user is reading, or null when nothing is selected.
 *  Outside a host, falls back to the demo scenario so flows stay demoable. */
export function getCurrentEmailId(): string | null {
  const Office = (globalThis as any).Office;
  const item = Office?.context?.mailbox?.item;
  if (item?.itemId) return item.itemId as string;
  if (inOutlook()) return null; // in Outlook with no item selected — say so
  return "pto-dana"; // browser/dev fallback only
}

export interface EmailMeta {
  id: string | null;
  subject: string;
  fromName: string;
}

/** Readable metadata for the context strip (sync; no body fetch). */
export function getCurrentEmailMeta(): EmailMeta {
  const Office = (globalThis as any).Office;
  const item = Office?.context?.mailbox?.item;
  if (item) {
    return {
      id: (item.itemId as string) ?? null,
      subject: (item.subject as string) || "(no subject)",
      fromName: item.from?.displayName || item.from?.emailAddress || "",
    };
  }
  // In Outlook with nothing selected: empty subject drives the pane's existing
  // "No message selected" state. Policy questions still work; email-specific
  // actions correctly have nothing to act on.
  if (inOutlook()) return { id: null, subject: "", fromName: "" };
  // Browser/dev fallback only.
  return { id: "pto-dana", subject: "PTO request for the first week of August", fromName: "Dana Williams" };
}

// One Office handler, fanned out to N subscribers.
//
// The previous version called addHandlerAsync directly and bailed silently when
// hostAvailable() was false at that instant — so on a slow launch the pinned pane
// simply never learned about item changes and showed a stale email forever. It
// also had no dedup (React StrictMode mounts effects twice, giving two handlers
// and two "Now viewing" lines) and no way to unsubscribe.
const itemChangedCallbacks = new Set<() => void>();
let officeHandlerBound = false;

/** Fire cb when the user selects a different message (pinned pane). Safe to call
 *  before the host is ready — binding is deferred until it is. Returns an
 *  unsubscribe function. */
export function onItemChanged(cb: () => void): () => void {
  itemChangedCallbacks.add(cb);
  bindItemChanged();
  return () => itemChangedCallbacks.delete(cb);
}

function bindItemChanged(): void {
  if (officeHandlerBound) return;
  const bind = () => {
    if (officeHandlerBound) return;
    const Office = (globalThis as any).Office;
    const mailbox = Office?.context?.mailbox;
    if (!mailbox?.addHandlerAsync || !Office?.EventType?.ItemChanged) return;
    officeHandlerBound = true;
    mailbox.addHandlerAsync(Office.EventType.ItemChanged, () => {
      // Recompute whether we're in the shared mailbox BEFORE the subscribers run:
      // the answer belongs to the item, so it is stale the instant the item changes,
      // and a stale value would attribute shared-mailbox turns to a personal one.
      refreshMailboxContext();
      for (const cb of [...itemChangedCallbacks]) cb();
    });
    // And once for the item the pane opened on, which fires no ItemChanged.
    refreshMailboxContext();
    console.info("[office] subscribed to ItemChanged");
  };
  if (hostAvailable()) bind();
  else onHostReady(bind);
}

/** A message from a multi-message selection. Shape mirrors what
 *  getSelectedItemsAsync returns (requirement set 1.13). */
export interface SelectedMessage {
  itemId: string;
  conversationId: string | null;
  /** Stable across mailboxes — this is what lets the backend find the same
   *  message inside the HR shared mailbox. */
  internetMessageId: string | null;
  subject: string;
  itemType: string | null;
  itemMode: string | null;
}

/** Messages the user has selected in the list.
 *
 *  In multi-select mode `Office.context.mailbox.item` is NULL — the single-item
 *  accessors above deliberately report "no message" there, and this is how you get
 *  the actual selection instead. Returns [] when unsupported or nothing usable is
 *  selected, so callers can treat it as "no multi-selection".
 *
 *  Requires Mailbox 1.13 (same set as SupportsNoItemContext), feature-detected. */
export function getSelectedMessages(): Promise<SelectedMessage[]> {
  const Office = (globalThis as any).Office;
  const mailbox = Office?.context?.mailbox;
  if (!hostAvailable() || typeof mailbox?.getSelectedItemsAsync !== "function") {
    return Promise.resolve([]);
  }
  return new Promise((resolve) => {
    try {
      mailbox.getSelectedItemsAsync((res: any) => {
        if (res?.status === "failed" || !Array.isArray(res?.value)) {
          console.warn("[office] getSelectedItemsAsync failed:", res?.error?.message);
          resolve([]);
          return;
        }
        resolve(
          res.value.map((m: any) => ({
            itemId: String(m?.itemId ?? ""),
            conversationId: m?.conversationId ?? null,
            internetMessageId: m?.internetMessageId ?? null,
            subject: m?.subject ?? "",
            itemType: m?.itemType ?? null,
            itemMode: m?.itemMode ?? null,
          })),
        );
      });
    } catch (err) {
      console.warn("[office] getSelectedItemsAsync threw:", err);
      resolve([]);
    }
  });
}

// SelectedItemsChanged — fires as the user adds/removes messages from the
// selection while the pane is open. Same fan-out + deferred-binding shape as
// ItemChanged and OfficeThemeChanged.
const selectionChangedCallbacks = new Set<() => void>();
let selectionHandlerBound = false;

/** Fire cb when the multi-message selection changes. Returns an unsubscribe.
 *  Safe to call before the host is ready; no-ops where unsupported. */
export function onSelectedItemsChanged(cb: () => void): () => void {
  selectionChangedCallbacks.add(cb);
  bindSelectionChanged();
  return () => selectionChangedCallbacks.delete(cb);
}

function bindSelectionChanged(): void {
  if (selectionHandlerBound) return;
  const bind = () => {
    if (selectionHandlerBound) return;
    const Office = (globalThis as any).Office;
    const mailbox = Office?.context?.mailbox;
    const evt = Office?.EventType?.SelectedItemsChanged;
    if (!mailbox?.addHandlerAsync || !evt) return; // unsupported on this host
    selectionHandlerBound = true;
    mailbox.addHandlerAsync(evt, () => {
      for (const cb of [...selectionChangedCallbacks]) cb();
    });
    console.info("[office] subscribed to SelectedItemsChanged");
  };
  if (hostAvailable()) bind();
  else onHostReady(bind);
}

/** Outlook's own theme, as "dark" | "light", or null when the host doesn't expose it.
 *
 *  Office.context.officeTheme is the authoritative signal and the reason we don't
 *  just use CSS prefers-color-scheme: Outlook has its OWN dark-mode toggle that is
 *  independent of the OS/browser setting, so the two routinely disagree. The pane
 *  is an iframe with its own document, so prefers-color-scheme reports the
 *  OS preference and would leave the pane light inside a dark Outlook.
 *
 *  Rather than trust a theme *name* (the values differ across hosts and versions),
 *  derive it from the background colour Outlook hands us. */
export function getOfficeThemeMode(): "dark" | "light" | null {
  const Office = (globalThis as any).Office;
  const theme = Office?.context?.officeTheme;
  const bg: unknown = theme?.bodyBackgroundColor;
  if (typeof bg !== "string" || !bg) return null;
  const lum = relativeLuminance(bg);
  if (lum === null) return null;
  return lum < 0.5 ? "dark" : "light";
}

/** Perceived luminance (0 = black, 1 = white) of "#rrggbb", "#rgb" or "rgb(r,g,b)". */
function relativeLuminance(color: string): number | null {
  const c = color.trim();
  let r: number;
  let g: number;
  let b: number;

  const hex = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(c);
  if (hex) {
    const h = hex[1];
    if (h.length === 3) {
      r = parseInt(h[0] + h[0], 16);
      g = parseInt(h[1] + h[1], 16);
      b = parseInt(h[2] + h[2], 16);
    } else {
      r = parseInt(h.slice(0, 2), 16);
      g = parseInt(h.slice(2, 4), 16);
      b = parseInt(h.slice(4, 6), 16);
    }
  } else {
    const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(c);
    if (!rgb) return null;
    r = Number(rgb[1]);
    g = Number(rgb[2]);
    b = Number(rgb[3]);
  }
  if ([r, g, b].some((v) => !Number.isFinite(v))) return null;
  // Rec. 601 luma — enough to classify light vs dark, and cheap.
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

// Outlook can change theme while the pane is open. Same fan-out/deferred-binding
// shape as ItemChanged, for the same reason: binding must not be lost to a race.
const themeChangedCallbacks = new Set<() => void>();
let themeHandlerBound = false;

/** Fire cb when Outlook's theme changes. Returns an unsubscribe. Safe to call
 *  before the host is ready; no-ops on hosts without OfficeThemeChanged. */
export function onOfficeThemeChanged(cb: () => void): () => void {
  themeChangedCallbacks.add(cb);
  bindThemeChanged();
  return () => themeChangedCallbacks.delete(cb);
}

function bindThemeChanged(): void {
  if (themeHandlerBound) return;
  const bind = () => {
    if (themeHandlerBound) return;
    const Office = (globalThis as any).Office;
    const mailbox = Office?.context?.mailbox;
    const evt = Office?.EventType?.OfficeThemeChanged;
    if (!mailbox?.addHandlerAsync || !evt) return; // not supported on this host
    themeHandlerBound = true;
    mailbox.addHandlerAsync(evt, () => {
      for (const cb of [...themeChangedCallbacks]) cb();
    });
    console.info("[office] subscribed to OfficeThemeChanged");
  };
  if (hostAvailable()) bind();
  else onHostReady(bind);
}

/** Test seam: reset module state between harness cases. Not used by the app. */
export function __resetOfficeStateForTests(): void {
  itemChangedCallbacks.clear();
  officeHandlerBound = false;
  themeChangedCallbacks.clear();
  themeHandlerBound = false;
  selectionChangedCallbacks.clear();
  selectionHandlerBound = false;
  lateReadyCallbacks.length = 0;
  watching = false;
}

/** The current item id in Graph REST format (what /api/mail expects).
 *  Office item ids are EWS-format; convertToRestId translates client-side. */
export function getRestItemId(): string | null {
  const Office = (globalThis as any).Office;
  const mailbox = Office?.context?.mailbox;
  const itemId = mailbox?.item?.itemId;
  if (!hostAvailable() || !itemId) return null;
  try {
    return mailbox.convertToRestId(itemId, Office.MailboxEnums.RestVersion.v2_0) as string;
  } catch {
    return itemId as string;
  }
}

/** The RFC-5322 Internet message id of the open message (e.g.
 *  "<abc123@contoso.com>"). Unlike the Office/EWS item id, this is identical on
 *  every copy of the email, so the backend can resolve the same message inside
 *  the HR shared mailbox regardless of which mailbox the user is reading from. */
export function getInternetMessageId(): string | null {
  const Office = (globalThis as any).Office;
  const item = Office?.context?.mailbox?.item;
  if (!hostAvailable() || !item) return null;
  const id = item.internetMessageId;
  return typeof id === "string" && id ? id : null;
}

/** Open a URL (e.g. a SharePoint document) in the browser from the pane. */
export function openUrl(url: string): void {
  const Office = (globalThis as any).Office;
  if (hostAvailable() && Office?.context?.ui?.openBrowserWindow) {
    Office.context.ui.openBrowserWindow(url);
  } else {
    window.open(url, "_blank", "noopener");
  }
}

// Re-exported, not redeclared. This was a THIRD copy of a shape that already exists
// in types.ts and in the backend's types.ts, and it silently went stale the moment a
// field was added to the other two -- which is exactly how it was found. The wire
// contract has one definition per package and this file is not a package.
import type { EmailContext } from "../types";
export type { EmailContext };

/** Read the currently-open message's real content (subject/from/body) from
 *  Office.js so the model works on the actual email. Returns null in a browser,
 *  where the backend falls back to mock content keyed by currentEmailId. */
export async function getCurrentEmail(): Promise<EmailContext | null> {
  const Office = (globalThis as any).Office;
  const item = Office?.context?.mailbox?.item;
  if (!hostAvailable() || !item?.body?.getAsync) return null;

  const subject: string = item.subject ?? "";
  const from: string = item.from ? `${item.from.displayName ?? ""} <${item.from.emailAddress ?? ""}>`.trim() : "";
  const body: string = await new Promise((resolve) => {
    item.body.getAsync("text", (res: any) => resolve(res?.status === "succeeded" ? (res.value as string) : ""));
  });
  // WHO THIS WAS SENT TO, which is how the pane knows which address to answer from.
  //
  // In read mode `to` and `cc` are plain arrays of EmailAddressDetails — no getAsync,
  // no requirement set beyond what we already declare. Addresses only; display names
  // are dropped because the only thing done with this is an equality test against the
  // configured shared mailbox, and a display name would just be more of someone's
  // identity travelling to the server for no reason.
  //
  // Defensive about shape: on some hosts these are absent rather than empty, and a
  // distribution list can yield an entry with no emailAddress at all.
  const addresses = (list: unknown): string[] =>
    Array.isArray(list) ? list.map((r: any) => String(r?.emailAddress ?? "").trim().toLowerCase()).filter(Boolean) : [];
  const recipients = [...addresses(item.to), ...addresses(item.cc)];
  return { subject, from, body, recipients };
}

/** Prefill Outlook's native reply editor with the approved draft — the
 *  "Open in Outlook editor" half of the hybrid send model. */
/** Plain draft text -> the htmlBody Office.js wants.
 *
 *  Both compose paths used to pass `text.replace(/\n/g, "<br>")` straight through,
 *  which treats the draft as HTML while the pane's preview treats it as plain text.
 *  Anything markup-shaped in the draft therefore rendered ONE way in the preview the
 *  user approves and ANOTHER way in the email that goes out — the mechanism behind a
 *  malformed link in a generated draft. An ordinary sentence was enough to break it:
 *  "salary < $50k" makes the rest of the paragraph a bogus tag.
 *
 *  The backend already strips link markup from drafts (normalizeDraftText). This is
 *  the second half of the same fix, and the one that matters even for text no model
 *  wrote — a user can edit the draft, and `&` must survive as `&`. */
// ===== shared:draftToHtmlBody (keep both copies identical) =====
const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** A raw HTML tag, a markdown link, or a bare URL, in that precedence order.
 *
 *  The tag alternative comes first and consumes a raw `<a href="...">` as ONE
 *  opaque token. Without it, the bare-URL branch below is free to match the URL
 *  sitting inside the href's own quotes and render it as a live, unvetted link —
 *  exactly the model-invented-SharePoint-URL case this function exists to block.
 *  That was masked rather than prevented before: the old bare-URL pattern needed
 *  two separately-quantified matches on the URL body, which happened to fail on
 *  anything shorter than `https://` + 2 characters, so a short test fixture never
 *  exposed it. Neither URL class admits `[`, for a similar reason one level up: a
 *  markdown-link URL that could reabsorb a `[` makes `\[...\]\(...\)` ambiguous
 *  about where the link ends whenever the text has no closing `)` for a while — a
 *  run of `[](http://` repeated made the engine retry a fresh backtrack search at
 *  every occurrence, which is what js/polynomial-redos caught.
 *
 *  Both the tag body (`[^>]`) and the markdown label (`[^\]\n]`) are bounded at
 *  2000 chars rather than left as `*`/`+`. Neither quantifier is ambiguous on its
 *  own, but each is followed by a required literal (`>`, `]`) that a crafted
 *  string can omit entirely, and re-trying that failed match at every subsequent
 *  starting position is quadratic overall -- confirmed by timing a draft made of
 *  `"<a ".repeat(40000)` (no `>` anywhere): the bound turns that from ~1.3s back
 *  to linear. No real tag or link label is anywhere near 2000 characters.
 *
 *  Trailing sentence punctuation ("...see https://x/policy.") is trimmed off the
 *  bare form in `draftToHtmlBody` itself now, not by a second quantified class
 *  here — that second class overlapped the first on everything but `.,;:!?`, the
 *  same shape of ambiguity from the other direction. */
const LINK_TOKEN = /<\/?[a-z][^>]{0,2000}>|\[([^\]\n]{0,2000})\]\(\s*(https?:\/\/[^)\s\[]+)\s*\)|(https?:\/\/[^\s<>"')\]]+)/gi;

/** Plain draft text -> the htmlBody Office.js wants.
 *
 *  Both compose paths used to pass `text.replace(/\n/g, "<br>")` straight through,
 *  which treats the draft as HTML while the pane's preview treats it as plain text.
 *  Anything markup-shaped in the draft therefore rendered ONE way in the preview the
 *  user approves and ANOTHER way in the email that goes out — the mechanism behind a
 *  malformed link in a generated draft. An ordinary sentence was enough to break it:
 *  "salary < $50k" makes the rest of the paragraph a bogus tag.
 *
 *  LINKS (2026-08-26). Two forms become real anchors and nothing else does:
 *    - `[label](https://…)`, which the backend has already vetted — normalizeDraftText
 *      only lets a markdown link keep its URL when that URL appears in the retrieved
 *      materials or the client's FAQ, and reduces every other one to its bare label.
 *    - a bare http(s) URL, which needs no vetting because the user can read exactly
 *      where it goes before they send.
 *
 *  RAW `<a href>` IS STILL ESCAPED TO VISIBLE TEXT, on purpose. That is the model's
 *  own markup and it is the form it invents SharePoint URLs in; letting it through
 *  here would put a live link to a document that may not exist into an email to an
 *  employee. Vetting happens on the server where the material list is; this function
 *  only renders what survived it, and it runs on text a user may have edited, so it
 *  cannot be the thing that decides what is trustworthy.
 *
 *  Tokenised BEFORE escaping rather than after. Escaping first turns `?a=1&b=2` into
 *  `?a=1&amp;b=2`, and an href built from that string is a different URL — the kind of
 *  bug that works on every example without a query string. */
export function draftToHtmlBody(text: string): string {
  const src = String(text);
  let out = "";
  let last = 0;
  LINK_TOKEN.lastIndex = 0;
  for (let m = LINK_TOKEN.exec(src); m; m = LINK_TOKEN.exec(src)) {
    out += esc(src.slice(last, m.index));
    if (m[3] !== undefined) {
      // Bare URL: trim trailing sentence punctuation off the href, but keep it in
      // the visible text — "...see https://x/policy." must not swallow the full
      // stop into the link.
      const trail = /[.,;:!?]+$/.exec(m[3])?.[0] ?? "";
      const url = m[3].slice(0, m[3].length - trail.length);
      out += `<a href="${esc(url)}">${esc(url)}</a>${esc(trail)}`;
    } else if (m[2] !== undefined) {
      const url = m[2];
      const label = m[1] !== undefined && m[1].trim() ? m[1] : url;
      out += `<a href="${esc(url)}">${esc(label)}</a>`;
    } else {
      // A raw HTML tag, most often a model-invented <a href>. Escaped verbatim as
      // one token so its attributes never reach the bare-URL branch above.
      out += esc(m[0]);
    }
    last = m.index + m[0].length;
  }
  out += esc(src.slice(last));
  return out.replace(/\n/g, "<br>");
}
// ===== end shared:draftToHtmlBody =====

/** Office.js caps a prefilled compose body at 32KB. Past it the call does not throw —
 *  the window opens EMPTY, which is indistinguishable from the bug below and just as
 *  silent. Checked on the encoded HTML, because that is what is actually transmitted
 *  and escaping can grow the string well past the plain-text length. */
const COMPOSE_BODY_LIMIT = 32 * 1024;

/** Why these return a reason instead of a boolean.
 *
 *  Reported 2026-08-26: "it opened in a new popout window, and then when I closed it
 *  and checked the drafts, the drafted reply was empty."
 *
 *  Both halves were real. The empty body came from App passing "" (see onAction), and
 *  `displayReplyForm` accepted it happily — an empty reply is a legal reply — so the
 *  pane reported "Draft placed in the Outlook reply editor" over a window containing
 *  nothing. A boolean return could not express that: it meant "we are in a host", not
 *  "the draft arrived". Anything that can hand the user an empty compose window while
 *  claiming success has to be able to say which of those it did. */
export type ComposeOutcome = "ok" | "no_host" | "empty" | "too_large" | "failed";

/** Open an EXISTING message in Outlook's own form — used for the draft Graph just
 *  created inside the shared mailbox.
 *
 *  This is the same class of API the personal path uses (`displayReplyForm`), and it
 *  is the only route to the HR From address in a NATIVE editor: Office.js cannot set
 *  From on a form it composes, but it does not have to here, because the item already
 *  carries it. Outlook's own Reply button behaves the same way — it inherits the
 *  mailbox context of the item it acts on (tested 2026-08-26).
 *
 *  THE PART THAT IS NOT KNOWN: whether a shared-mailbox item id is addressable this
 *  way. `displayMessageForm` documents no restriction on drafts or on other mailboxes,
 *  but it takes an EWS id while Graph hands us a REST one, and Microsoft states that
 *  EWS is unsupported in shared-mailbox scenarios — a statement about the EWS API
 *  surface that may or may not extend to the id format. So this tries, in order:
 *
 *    1. the Graph id as given,
 *    2. the same id run through `convertToEwsId`,
 *
 *  and reports failure rather than papering over it. `displayMessageFormAsync` is used
 *  where available precisely so failure is OBSERVABLE — the callback-less form returns
 *  nothing and would let a silent no-op read as success, which is the same trap the
 *  empty-draft bug fell into.
 *
 *  Deliberately does NOT fall back to opening a browser window. The caller degrades to
 *  telling the user where the draft is, which is honest and keeps them inside Outlook. */
/** One id candidate's result. NO IDS AND NO CONTENT — the `kind` says which candidate
 *  it was, which is the only part anyone needs, and mailbox item identifiers are not
 *  something to scatter through logs. */
export interface ComposeAttempt {
  kind: "server-ews" | "graph-rest" | "client-ews";
  ok: boolean;
  /** Office's own error code, which is the actually diagnostic part. */
  code?: number | string;
  message?: string;
}
export interface ComposeTrace {
  outcome: ComposeOutcome;
  attempts: ComposeAttempt[];
  /** Which API the host offered. `displayMessageForm` cannot report success, so a
   *  trace on that path is weaker evidence and should say so. */
  api: "async" | "sync" | "none";
}

export async function displayExistingMessage(graphItemId: string, serverEwsId?: string | null): Promise<ComposeTrace> {
  const Office = (globalThis as any).Office;
  const mailbox = Office?.context?.mailbox;
  const attempts: ComposeAttempt[] = [];
  const api: ComposeTrace["api"] = mailbox?.displayMessageFormAsync ? "async" : mailbox?.displayMessageForm ? "sync" : "none";
  if (!hostAvailable() || !mailbox) return { outcome: "no_host", attempts, api: "none" };

  const attempt = (kind: ComposeAttempt["kind"], id: string): Promise<boolean> =>
    new Promise((resolve) => {
      try {
        if (mailbox.displayMessageFormAsync) {
          mailbox.displayMessageFormAsync(id, (res: any) => {
            const ok = res?.status === "succeeded" || res?.status === Office?.AsyncResultStatus?.Succeeded;
            attempts.push({ kind, ok, code: res?.error?.code, message: res?.error?.message });
            if (!ok) console.warn("[office] displayMessageFormAsync failed:", kind, res?.error?.code, res?.error?.message);
            resolve(!!ok);
          });
          return;
        }
        if (mailbox.displayMessageForm) {
          // No callback on this form, so success cannot be confirmed — only the
          // absence of a throw. Reported as a success because there is nothing else
          // to go on; the async form above is the one that can tell us the truth,
          // which is why the trace records WHICH api was used.
          mailbox.displayMessageForm(id);
          attempts.push({ kind, ok: true, message: "sync api — success not confirmable" });
          resolve(true);
          return;
        }
        attempts.push({ kind, ok: false, message: "host exposes neither displayMessageForm variant" });
        resolve(false);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        attempts.push({ kind, ok: false, message });
        console.warn("[office] displayMessageForm threw:", kind, message);
        resolve(false);
      }
    });

  // THE RAW GRAPH ID FIRST, because it is the one we have actually seen work.
  //
  // Measured on dev 2026-08-27: `succeededWith: "graph-rest"`, via
  // displayMessageFormAsync (the variant that can confirm success). Outlook accepts a
  // REST id for a draft in another mailbox directly — no conversion of any kind.
  //
  // This inverts the order it shipped with, and the correction is the point. The
  // reasoning said an EWS id translated against the owning mailbox was most likely to
  // work, so that went first. The evidence says otherwise, and an ordering built on
  // reasoning would have kept an UNPROVEN path ahead of a proven one. The
  // `translateExchangeIds` call that produced the server id is gone entirely — it
  // 403'd on every single draft (our app-only permissions do not cover it) and was
  // never needed.
  if (await attempt("graph-rest", graphItemId)) return { outcome: "ok", attempts, api };

  // The remaining two are fallbacks for hosts we have not tested. One success on one
  // client is not a platform guarantee — Outlook for Mac has a reported case where
  // this API reports success without displaying anything — so the cheap alternatives
  // stay until there is evidence from more than one place.
  if (serverEwsId && (await attempt("server-ews", serverEwsId))) return { outcome: "ok", attempts, api };

  try {
    const ews = mailbox.convertToEwsId?.(graphItemId, Office?.MailboxEnums?.RestVersion?.v2_0);
    if (ews && ews !== graphItemId && ews !== serverEwsId && (await attempt("client-ews", ews))) {
      return { outcome: "ok", attempts, api };
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    attempts.push({ kind: "client-ews", ok: false, message });
    console.warn("[office] convertToEwsId threw:", message);
  }
  return { outcome: "failed", attempts, api };
}

export function displayReplyForm(text: string): ComposeOutcome {
  const Office = (globalThis as any).Office;
  const item = Office?.context?.mailbox?.item;
  const body = draftToHtmlBody(text);
  // Refuse BEFORE opening anything. Opening an empty reply and then apologising still
  // costs the user a stray window to close, and in a popped-out compose that is the
  // most annoying possible place to be told something went wrong.
  if (!text.trim()) return "empty";
  if (body.length > COMPOSE_BODY_LIMIT) return "too_large";
  if (!hostAvailable()) return "no_host";

  // displayReplyFormAsync (Mailbox 1.9) reports whether Outlook actually accepted the
  // form; the callback-less displayReplyForm cannot. The task pane declares 1.13 in
  // VersionOverrides so the async form is present in every client we support, but the
  // fallback stays for the 1.5 base requirement set and for the mock host in tests.
  // REPLY-ALL, not reply-to-sender (2026-09-09). An HR thread routinely carries a
  // manager, a benefits vendor or a second HR address on it, and a reply that drops
  // them reads to everyone else as no reply at all. The human still reviews the
  // recipient list in Outlook's own editor before sending, so widening the default
  // is recoverable; silently narrowing it is not. The Graph path does the same via
  // `replyAll` (see api/client.ts) — the two compose paths must stay identical.
  //
  // Falls back to the reply-to-sender forms rather than failing: an older host
  // without the ReplyAll variants should still produce a draft.
  if (item?.displayReplyAllFormAsync || item?.displayReplyFormAsync) {
    const call = item.displayReplyAllFormAsync ? "displayReplyAllFormAsync" : "displayReplyFormAsync";
    item[call]({ htmlBody: body }, (res: any) => {
      if (res?.status !== "succeeded") {
        console.error(`[office] ${call} failed:`, res?.error?.code, res?.error?.message);
      }
    });
    return "ok";
  }
  if (item?.displayReplyAllForm || item?.displayReplyForm) {
    (item.displayReplyAllForm ? item.displayReplyAllForm : item.displayReplyForm).call(item, { htmlBody: body });
    return "ok";
  }
  return "no_host";
}

/** Open Outlook's native NEW-message compose window prefilled with a
 *  model-drafted subject + body (the "draft from scratch" flow). */
export function displayNewMessageForm(subject: string, text: string): ComposeOutcome {
  const Office = (globalThis as any).Office;
  const mailbox = Office?.context?.mailbox;
  const body = draftToHtmlBody(text);
  if (!text.trim()) return "empty";
  if (body.length > COMPOSE_BODY_LIMIT) return "too_large";
  if (hostAvailable() && mailbox?.displayNewMessageForm) {
    mailbox.displayNewMessageForm({ subject: subject || "", htmlBody: body });
    return "ok";
  }
  return "no_host";
}

/** The Office SSO error code from the most recent getAccessToken() attempt, or
 *  null if the last attempt succeeded (or SSO isn't available at all).
 *
 *  Exposed so the pane can say something TRUE about why a 401 happened. Every
 *  cause otherwise renders as the same "Something went wrong" bubble, which is how
 *  "not assigned to the Entra app" (13005-class) was reported as "the assistant is
 *  not responding". The console line is not enough — nobody opens devtools in a
 *  task pane during a demo. */
let lastSsoError: number | null = null;
export function getLastSsoError(): number | null {
  return lastSsoError;
}

/** Entra SSO token for the backend. Returns null when SSO is unavailable or
 *  fails, in which case client.ts omits the Authorization header and the backend
 *  answers 401 missing_token (when REQUIRE_AUTH=true). */
/** The token request currently in flight, if any.
 *
 *  Office.js allows ONE outstanding getAccessToken call per add-in. A second
 *  concurrent call fails with **13008** — "this add-in is already requesting an
 *  access token" — and we were causing that ourselves: authHeaders() calls this on
 *  every request, and the pane fires several at mount (the history probe, the
 *  conversation load). Seen live in an Outlook web console 2026-08-10, immediately
 *  followed by `/api/conversations 401`.
 *
 *  So: single-flight. Concurrent callers share one request and one answer. Not a
 *  token CACHE — Office.js already caches and handles expiry, and duplicating that
 *  here would mean serving a stale token past its lifetime. This only collapses
 *  calls that overlap in time. */
let inFlightToken: Promise<string | null> | null = null;

export function getAccessToken(): Promise<string | null> {
  if (inFlightToken) return inFlightToken;
  inFlightToken = acquireAccessToken().finally(() => {
    inFlightToken = null;
  });
  return inFlightToken;
}

async function acquireAccessToken(): Promise<string | null> {
  const OfficeRuntime = (globalThis as any).OfficeRuntime;
  // The API object can exist while Office.js is still wiring itself up, and calling
  // it then throws `Cannot read properties of undefined (reading
  // 'getAccessTokenAsync')` from inside Office's own code — which surfaces as a bare
  // 401 with no error code, the least diagnosable shape this failure has. Waiting
  // for the mailbox context avoids it; callers already retry on NotAuthenticatedYet.
  if (!hostAvailable() || !OfficeRuntime?.auth?.getAccessToken) {
    console.warn("[sso] getAccessToken unavailable", { inHost: hostAvailable(), hasApi: !!OfficeRuntime?.auth?.getAccessToken });
    lastSsoError = null;
    return null;
  }
  try {
    // allowConsentPrompt matters as much as allowSignInPrompt and was missing.
    // Without it, the FIRST user who has never consented to `access_as_user` gets
    // 13005/13002 and no token — Office won't offer the consent dialog unless
    // asked. The user is signed in, so allowSignInPrompt alone changes nothing,
    // and the pane just looks broken. Safe to request: the scope is User-consent
    // ("Access BayMark HR Assistant as you"), not an admin-only permission.
    const token = await OfficeRuntime.auth.getAccessToken({ allowSignInPrompt: true, allowConsentPrompt: true });
    lastSsoError = null;
    return token;
  } catch (err: any) {
    lastSsoError = typeof err?.code === "number" ? err.code : null;
    // Log err.code — this used to be a bare `catch { return null }`, which threw
    // away the ONLY piece of information that distinguishes these causes. Every
    // one of them otherwise presents identically as "chat failed: 401".
    //   13000 getAccessToken unsupported by this Outlook build
    //   13001 user not signed in to Office   13002 consent prompt aborted
    //   13003 unsupported user type (MSA/anonymous)  13005 grant not consented
    //   13006 / 13008 / 13010 transient / in-progress / unsupported iframe
    //   13012 unsupported context (often localhost or a tunnel)
    console.error("[sso] getAccessToken failed — code:", err?.code, "message:", err?.message ?? err);
    return null;
  }
}

/* --------------------------------------------------------------------------
   Turn context, for analytics only. Nothing here is authorized on, and nothing
   here changes an answer — the backend normalizes both values to a closed set
   before they reach the database (routes/chat.ts).
   -------------------------------------------------------------------------- */

/** Which Outlook this is: OutlookWebApp, OutlookWindows, OutlookMac, …
 *
 *  IT WAS READING THE WRONG API, AND THE FAILURE WAS SILENT. Until 2026-08-28 this
 *  returned `Office.context.diagnostics.hostName` — the COMMON API, whose hostName
 *  is the Office application ("Outlook"), not the client. Every prod row landed as
 *  `unreported`, and anything that did arrive would have been normalised to `other`
 *  by the allowlist in routes/chat.ts, which expects the OUTLOOK API's values.
 *  Nothing failed; the column just filled with nulls, and the dashboard panel built
 *  on it could only ever have shown one bar.
 *
 *  Found while trying to answer whether BayMark's clients support Nested App Auth,
 *  which is exactly the question this field exists to answer.
 *
 *  TWO SOURCES, BECAUSE NEITHER IS ENOUGH ALONE. `mailbox.diagnostics.hostName`
 *  distinguishes web / new Outlook / mobile but reports plain "Outlook" for BOTH
 *  Windows and Mac desktop — and that split is precisely what an NAA support
 *  decision turns on. `Office.context.diagnostics.platform` ("PC" / "Mac") breaks
 *  the tie. Values are chosen to match the allowlist rather than invented here; an
 *  unrecognised one becomes `other` there rather than reaching the database raw.
 *
 *  Defensive throughout: the pane also renders in a plain browser for local review,
 *  where none of this exists. */
export function getClientPlatform(): string | null {
  const Office = (globalThis as any).Office;
  const host = Office?.context?.mailbox?.diagnostics?.hostName;
  if (typeof host !== "string" || !host) return null;

  // Already unambiguous: web, new Outlook for Windows, and the two mobile clients
  // each report a distinct hostName.
  if (host !== "Outlook") return host;

  // Classic desktop reports "Outlook" on both platforms. Split it.
  const platform = Office?.context?.diagnostics?.platform;
  if (platform === "Mac") return "OutlookMac";
  if (platform === "PC") return "OutlookWindows";
  return "OutlookWindows";
}

/** Cached result of the shared-mailbox probe.
 *
 *  `getSharedPropertiesAsync` is an async round trip to the host, and the answer
 *  cannot change without the item changing — so this is recomputed on item change
 *  (below) rather than on every turn, which would put a host call on the critical
 *  path of every message the user sends. */
let mailboxContext: "shared" | "own" | null = null;

export function getMailboxContext(): "shared" | "own" | null {
  return mailboxContext;
}



/** Work out whether the open item lives in the HR SHARED mailbox or the user's own.
 *
 *  This is the only way to answer "is anyone actually using the add-in in the shared
 *  mailbox", which is the question the whole SupportsSharedFolders rollout turns on.
 *
 *  `getSharedPropertiesAsync` FAILS — rather than returning a flag — when the item is
 *  in the user's own mailbox. That is the documented contract, not an error worth
 *  logging: Microsoft's own sample treats a failed result as "not shared". So a
 *  failure maps to "own", and only a genuine absence of host or item leaves it null.
 *
 *  Requires <SupportsSharedFolders>true</SupportsSharedFolders> in the manifest
 *  (added in 1.1.4) — without it the API is not merely unavailable, the add-in never
 *  activates in a shared mailbox at all, so this would never be called there. */
export function refreshMailboxContext(): void {
  const Office = (globalThis as any).Office;
  const item = Office?.context?.mailbox?.item;
  if (!hostAvailable() || !item?.getSharedPropertiesAsync) {
    mailboxContext = null;
    return;
  }
  try {
    item.getSharedPropertiesAsync((result: any) => {
      const failed = result?.status === "failed" || result?.status === Office?.AsyncResultStatus?.Failed;
      mailboxContext = failed || !result?.value ? "own" : "shared";
    });
  } catch {
    // A host that throws synchronously tells us nothing either way. Null, not "own":
    // guessing here would quietly report shared-mailbox usage as personal.
    mailboxContext = null;
  }
}
